import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, query, orderBy, serverTimestamp
} from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import LoadingSpinner from './components/LoadingSpinner';
import './AccountRequestsApp.css';

function AccountRequestsApp() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [applications, setApplications] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    reason: '',
    requestedRole: 'User',
    requestedApplications: []
  });

  const role = userProfile?.role || null;
  const isAdmin = role === 'Admin';

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile({ id: snap.id, ...snap.data() });
          }
          setAuthLoading(false);
        });
        return () => profileUnsub();
      } else {
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Set page title
  useEffect(() => {
    document.title = 'Account Requests - Wixon & Cross Desk Suite';
  }, []);

  // Applications listener - load even if not authenticated (needed for public request form)
  useEffect(() => {
    const q = query(collection(db, 'applications'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // Account requests listener
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'accountRequests'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const reqs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRequests(reqs);
    });
    return () => unsub();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'accountRequests'), {
        ...formData,
        status: 'Pending',
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        createdByEmail: user?.email || formData.email
      });
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        reason: '',
        requestedRole: 'User',
        requestedApplications: []
      });
      setIsFormOpen(false);
      alert('Account request submitted! An administrator will review your request.');
    } catch (err) {
      console.error('Error submitting request:', err);
      alert('Error submitting request');
    }
  };

  const handleApprove = async (request) => {
    if (!isAdmin) return;
    if (!window.confirm(`Approve account request for ${request.email}?`)) return;
    
    try {
      await updateDoc(doc(db, 'accountRequests', request.id), {
        status: 'Approved',
        approvedAt: serverTimestamp(),
        approvedBy: user.uid
      });
      alert('Request approved! Admin should create the account manually.');
    } catch (err) {
      console.error('Error approving:', err);
      alert('Error approving request');
    }
  };

  const handleReject = async (request) => {
    if (!isAdmin) return;
    const reason = prompt('Rejection reason (optional):');
    if (reason === null) return;
    
    try {
      await updateDoc(doc(db, 'accountRequests', request.id), {
        status: 'Rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: user.uid,
        rejectionReason: reason
      });
    } catch (err) {
      console.error('Error rejecting:', err);
      alert('Error rejecting request');
    }
  };

  const handleDelete = async (request) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete request for ${request.email}?`)) return;
    
    try {
      await deleteDoc(doc(db, 'accountRequests', request.id));
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error deleting request');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      alert('Error signing out');
    }
  };

  if (authLoading) return <LoadingSpinner message="Loading..." />;

  const pendingRequests = requests.filter(r => r.status === 'Pending');
  const approvedRequests = requests.filter(r => r.status === 'Approved');
  const rejectedRequests = requests.filter(r => r.status === 'Rejected');

  // Public view - just show the request form
  if (!user || !userProfile) {
    return (
      <div className="account-requests-app">
        <header className="app-header" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '2rem'}}>
          <div className="header-content" style={{maxWidth: '800px', margin: '0 auto', textAlign: 'center'}}>
            <h1 style={{fontSize: '2rem', marginBottom: '0.5rem'}}>Request Account Access</h1>
            <p style={{fontSize: '1.125rem', opacity: 0.9}}>Submit a request to gain access to the Wixon & Cross Desk Suite</p>
          </div>
        </header>

        <main className="app-main" style={{maxWidth: '600px', margin: '2rem auto', padding: '0 1rem'}}>
          <div style={{background: 'white', borderRadius: '0.5rem', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="your.email@example.com"
                />
              </div>
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={e => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={e => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Requested Applications</label>
                <div style={{
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem', 
                  padding: '0.75rem',
                  backgroundColor: '#f9fafb'
                }}>
                  {applications.length === 0 ? (
                    <p style={{color: '#6b7280', fontSize: '0.875rem', margin: 0}}>Loading applications...</p>
                  ) : (
                    applications.map(app => (
                      <label 
                        key={app.id} 
                        style={{
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.75rem', 
                          padding: '0.5rem', 
                          cursor: 'pointer',
                          borderRadius: '0.25rem',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={formData.requestedApplications.includes(app.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                requestedApplications: [...formData.requestedApplications, app.name]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                requestedApplications: formData.requestedApplications.filter(a => a !== app.name)
                              });
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            flexShrink: 0,
                            width: '16px',
                            height: '16px',
                            marginRight: '0.25rem'
                          }}
                        />
                        <span style={{
                          fontSize: '0.9375rem', 
                          color: '#111827',
                          lineHeight: '1.5',
                          userSelect: 'none'
                        }}>
                          {app.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>Select all applications you need access to</p>
              </div>
              <div className="form-group">
                <label>Reason for Request *</label>
                <textarea
                  value={formData.reason}
                  onChange={e => setFormData({...formData, reason: e.target.value})}
                  rows="4"
                  required
                  placeholder="Please explain why you need access..."
                />
              </div>
              <div style={{display: 'flex', gap: '1rem', marginTop: '1.5rem'}}>
                <button type="submit" className="btn btn-primary" style={{flex: 1}}>
                  Submit Request
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
          
          <div style={{textAlign: 'center', marginTop: '2rem'}}>
            <p style={{color: '#6b7280', fontSize: '0.875rem'}}>
              Already have an account? <button onClick={() => navigate('/signin')} style={{color: '#667eea', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline'}}>Sign In</button>
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Admin/authenticated view
  return (
    <div className="account-requests-app">
      <header className="app-header">
        <div className="header-content">
          <div className="breadcrumb-nav-app">
            <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
              🖥️ Desktop
            </button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
              📦 Dashboard
            </button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item breadcrumb-current">📝 Account Requests</span>
          </div>
          <div className="header-actions">
            <span className="user-email">{user.email}</span>
            <span className="user-role-badge">{role}</span>
            <button className="btn-signout" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="requests-container">
          <div className="requests-header">
            <h2>New Account Requests</h2>
            <button className="btn btn-primary" onClick={() => setIsFormOpen(true)}>
              + New Request
            </button>
          </div>

          <div className="stats-row">
            <div className="stat-card stat-card--gradient">
              <div className="stat-value">{pendingRequests.length}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card stat-card--gradient">
              <div className="stat-value">{approvedRequests.length}</div>
              <div className="stat-label">Approved</div>
            </div>
            <div className="stat-card stat-card--gradient">
              <div className="stat-value">{rejectedRequests.length}</div>
              <div className="stat-label">Rejected</div>
            </div>
          </div>

          <div className="requests-sections">
            {/* Pending Requests */}
            <div className="requests-section">
              <h3>Pending Requests ({pendingRequests.length})</h3>
              {pendingRequests.length === 0 ? (
                <p className="empty-message">No pending requests</p>
              ) : (
                <div className="requests-list">
                  {pendingRequests.map(req => (
                    <div key={req.id} className="request-card pending">
                      <div className="request-header">
                        <div>
                          <h4>{req.firstName} {req.lastName}</h4>
                          <p className="request-email">{req.email}</p>
                        </div>
                        <span className="status-badge status-pending">Pending</span>
                      </div>
                      <div className="request-body">
                        <p><strong>Requested Role:</strong> {req.requestedRole}</p>
                        {req.requestedApplications && req.requestedApplications.length > 0 && (
                          <p><strong>Requested Applications:</strong> {req.requestedApplications.join(', ')}</p>
                        )}
                        <p><strong>Reason:</strong> {req.reason}</p>
                        <p><strong>Submitted:</strong> {req.createdAt?.toDate().toLocaleString()}</p>
                        <p><strong>Submitted By:</strong> {req.createdByEmail}</p>
                      </div>
                      {isAdmin && (
                        <div className="request-actions">
                          <button className="btn btn-success" onClick={() => handleApprove(req)}>
                            Approve
                          </button>
                          <button className="btn btn-danger" onClick={() => handleReject(req)}>
                            Reject
                          </button>
                          <button className="btn btn-secondary" onClick={() => handleDelete(req)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Approved Requests */}
            <div className="requests-section">
              <h3>Approved Requests ({approvedRequests.length})</h3>
              {approvedRequests.length === 0 ? (
                <p className="empty-message">No approved requests</p>
              ) : (
                <div className="requests-list">
                  {approvedRequests.map(req => (
                    <div key={req.id} className="request-card approved">
                      <div className="request-header">
                        <div>
                          <h4>{req.firstName} {req.lastName}</h4>
                          <p className="request-email">{req.email}</p>
                        </div>
                        <span className="status-badge status-approved">Approved</span>
                      </div>
                      <div className="request-body">
                        <p><strong>Role:</strong> {req.requestedRole}</p>
                        {req.requestedApplications && req.requestedApplications.length > 0 && (
                          <p><strong>Applications:</strong> {req.requestedApplications.join(', ')}</p>
                        )}
                        <p><strong>Approved:</strong> {req.approvedAt?.toDate().toLocaleString()}</p>
                      </div>
                      {isAdmin && (
                        <div className="request-actions">
                          <button className="btn btn-secondary" onClick={() => handleDelete(req)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rejected Requests */}
            <div className="requests-section">
              <h3>Rejected Requests ({rejectedRequests.length})</h3>
              {rejectedRequests.length === 0 ? (
                <p className="empty-message">No rejected requests</p>
              ) : (
                <div className="requests-list">
                  {rejectedRequests.map(req => (
                    <div key={req.id} className="request-card rejected">
                      <div className="request-header">
                        <div>
                          <h4>{req.firstName} {req.lastName}</h4>
                          <p className="request-email">{req.email}</p>
                        </div>
                        <span className="status-badge status-rejected">Rejected</span>
                      </div>
                      <div className="request-body">
                        {req.requestedApplications && req.requestedApplications.length > 0 && (
                          <p><strong>Requested Applications:</strong> {req.requestedApplications.join(', ')}</p>
                        )}
                        <p><strong>Reason for Rejection:</strong> {req.rejectionReason || 'N/A'}</p>
                        <p><strong>Rejected:</strong> {req.rejectedAt?.toDate().toLocaleString()}</p>
                      </div>
                      {isAdmin && (
                        <div className="request-actions">
                          <button className="btn btn-secondary" onClick={() => handleDelete(req)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* New Request Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay" onClick={() => setIsFormOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>New Account Request</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={e => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={e => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Requested Applications</label>
                <div style={{
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem', 
                  padding: '0.75rem',
                  backgroundColor: '#f9fafb'
                }}>
                  {applications.length === 0 ? (
                    <p style={{color: '#6b7280', fontSize: '0.875rem', margin: 0}}>No applications available</p>
                  ) : (
                    applications.map(app => (
                      <label 
                        key={app.id} 
                        style={{
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.75rem', 
                          padding: '0.5rem', 
                          cursor: 'pointer',
                          borderRadius: '0.25rem',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={formData.requestedApplications.includes(app.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                requestedApplications: [...formData.requestedApplications, app.name]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                requestedApplications: formData.requestedApplications.filter(a => a !== app.name)
                              });
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            flexShrink: 0,
                            width: '16px',
                            height: '16px',
                            marginRight: '0.25rem'
                          }}
                        />
                        <span style={{
                          fontSize: '0.9375rem', 
                          color: '#111827',
                          lineHeight: '1.5',
                          userSelect: 'none'
                        }}>
                          {app.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>Select all applications you need access to</p>
              </div>
              <div className="form-group">
                <label>Requested Role *</label>
                <select
                  value={formData.requestedRole}
                  onChange={e => setFormData({...formData, requestedRole: e.target.value})}
                  required
                >
                  <option value="User">User</option>
                  <option value="Developer">Developer</option>
                  <option value="Tester/QA">Tester/QA</option>
                  {isAdmin && <option value="Admin">Admin</option>}
                </select>
              </div>
              <div className="form-group">
                <label>Reason for Request *</label>
                <textarea
                  value={formData.reason}
                  onChange={e => setFormData({...formData, reason: e.target.value})}
                  rows="4"
                  required
                  placeholder="Why is this account needed?"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Submit Request</button>
                <button type="button" className="btn btn-secondary" onClick={() => setIsFormOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountRequestsApp;
